"use client";

import { Facebook, Instagram, MessageCircle, CheckCircle, XCircle, Mail } from "lucide-react";
import Link from "next/link";
import React, { useState } from "react";
import Modal from "@/components/ui/Modal";
import api from "@/lib/axiosInstance";

const Footer = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState<"success" | "error" | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [nameError, setNameError] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const validateEmail = (email: string): boolean => {
    // Clear previous error
    setEmailError("");

    // Check if email is empty
    if (!email.trim()) {
      setEmailError("Email address is required");
      return false;
    }

    // Check email format with comprehensive regex
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(email)) {
      setEmailError("Please enter a valid email address");
      return false;
    }

    // Check for common typos
    const commonDomains = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com'];
    const domain = email.split('@')[1]?.toLowerCase();
    
    if (domain && domain.includes('..')) {
      setEmailError("Email address contains invalid characters");
      return false;
    }

    return true;
  };

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setNameError("");
    if (!name.trim()) {
      setNameError("Your name is required");
      return;
    }
    if (!validateEmail(email)) {
      return;
    }

    setIsLoading(true);
    setErrorMessage("");

    try {
      const response = await api.post('/newsletter/subscribe', { email, name: name.trim() });

      if (response.status === 200 || response.status === 201) {
        setSubscriptionStatus("success");
        setEmail("");
        setName("");
        setIsModalOpen(true);
      }
    } catch (error: any) {
      console.error('Newsletter subscription error:', error);
      
      // Check if email already exists
      if (error.response?.status === 409) {
        setEmailError("This email is already subscribed");
        setErrorMessage("This email is already subscribed to our newsletter");
      } else if (error.response?.status === 400) {
        setEmailError(error.response?.data?.msg || "Invalid email address");
        setErrorMessage(error.response?.data?.msg || "Please enter a valid email address");
      } else {
        setErrorMessage(error.response?.data?.msg || "Unable to subscribe at this time. Please try again later.");
      }
      
      setSubscriptionStatus("error");
      setIsModalOpen(true);
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <footer className="bg-[#001a00] text-white">
      {/* Main footer content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-6 gap-10 lg:gap-6">

          {/* Brand column — full width on mobile, 2 cols on desktop */}
          <div className="col-span-1 lg:col-span-2">
            <Link href="/">
              <img
                src="/logo.png"
                alt="Ren Power Group Logo"
                className="w-36 h-auto cursor-pointer hover:opacity-80 transition-opacity brightness-0 invert"
              />
            </Link>
            <p className="text-white/60 text-sm leading-relaxed mt-4 max-w-xs">
              Nigeria's solar marketplace connecting homeowners, manufacturers, distributors, and recyclers under one verified platform.
            </p>

            <div className="mt-5">
              <h4 className="text-xs font-semibold text-white/40 uppercase tracking-widest mb-2">Customer Support</h4>
              <a
                href="mailto:admin@renpowerltd.com"
                className="flex items-center gap-2 text-white/70 text-sm hover:text-white transition-colors"
              >
                <Mail className="w-3.5 h-3.5 text-[#FFB600] flex-shrink-0" />
                admin@renpowerltd.com
              </a>
            </div>

            {/* Socials under customer support */}
            <div className="flex items-center gap-3 mt-5">
              <a
                href="https://web.facebook.com/profile.php?id=61553076221891"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-[#FFB600] flex items-center justify-center transition-colors duration-200 group"
                aria-label="Facebook"
              >
                <Facebook size={15} className="group-hover:text-black transition-colors" />
              </a>
              <a
                href="https://www.instagram.com/renpowerltd/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-[#FFB600] flex items-center justify-center transition-colors duration-200 group"
                aria-label="Instagram"
              >
                <Instagram size={15} className="group-hover:text-black transition-colors" />
              </a>
              <a
                href="https://wa.me/2348161243325"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-[#FFB600] flex items-center justify-center transition-colors duration-200 group"
                aria-label="WhatsApp"
              >
                <MessageCircle size={15} className="group-hover:text-black transition-colors" />
              </a>
            </div>
          </div>

          {/* 4 link columns — 2x2 on mobile, individual cols on desktop via lg:contents */}
          <div className="grid grid-cols-2 gap-8 lg:contents">

          {/* Quick Links */}
          <div>
            <h3 className="text-sm font-semibold text-white/90 mb-4 uppercase tracking-wider">Quick Links</h3>
            <ul className="space-y-2.5">
              {[
                { href: "/marketplace", label: "Marketplace" },
                { href: "/consultation", label: "Consultation" },
                { href: "/financing", label: "Solar Financing" },
                { href: "/solar-calculator", label: "Solar Calculator" },
                { href: "/recyclers", label: "Find Recyclers" },
                { href: "/auth/register", label: "Get Started" },
                { href: "/auth/login", label: "Sign In" },
              ].map(({ href, label }) => (
                <li key={label}>
                  <Link href={href} className="text-sm text-white/55 hover:text-white transition-colors duration-200">
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="text-sm font-semibold text-white/90 mb-4 uppercase tracking-wider">Company</h3>
            <ul className="space-y-2.5">
              {[
                { href: "/about", label: "About Us" },
                { href: "/blog", label: "Solar News" },
                { href: "/success-stories", label: "Success Stories" },
                { href: "/faqs", label: "FAQ" },
                { href: "/contact", label: "Contact" },
                { href: "/privacy-policy", label: "Privacy Policy" },
                { href: "/terms-of-service", label: "Terms of Service" },
              ].map(({ href, label }) => (
                <li key={label}>
                  <Link href={href} className="text-sm text-white/55 hover:text-white transition-colors duration-200">
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Platform */}
          <div>
            <h3 className="text-sm font-semibold text-white/90 mb-4 uppercase tracking-wider">Platform</h3>
            <ul className="space-y-2.5">
              {[
                { href: "/for-manufacturers", label: "For Manufacturers" },
                { href: "/for-distributors", label: "For Distributors" },
                { href: "/for-consumers", label: "For Homeowners" },
                { href: "/for-recyclers", label: "For Recyclers" },
              ].map(({ href, label }) => (
                <li key={label}>
                  <Link href={href} className="text-sm text-white/55 hover:text-white transition-colors duration-200">
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h3 className="text-sm font-semibold text-white/90 mb-4 uppercase tracking-wider">Newsletter</h3>
            <p className="text-sm text-white/55 leading-relaxed mb-4">
              Get solar news, platform updates, and market insights delivered to your inbox.
            </p>
            <form onSubmit={handleNewsletterSubmit} className="space-y-2">
              <input
                type="text"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (nameError) setNameError("");
                }}
                placeholder="Your name"
                className={`w-full px-4 py-2.5 bg-white/10 border rounded-xl text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 transition-all ${
                  nameError
                    ? "border-red-500/60 focus:ring-red-500/30"
                    : "border-white/20 focus:border-[#FFB600]/60 focus:ring-[#FFB600]/20"
                }`}
              />
              {nameError && (
                <p className="text-red-400 text-xs flex items-center gap-1">
                  <XCircle className="w-3 h-3 flex-shrink-0" />
                  {nameError}
                </p>
              )}
              <input
                type="text"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (emailError) setEmailError("");
                }}
                placeholder="you@email.com"
                className={`w-full px-4 py-2.5 bg-white/10 border rounded-xl text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 transition-all ${
                  emailError
                    ? "border-red-500/60 focus:ring-red-500/30"
                    : "border-white/20 focus:border-[#FFB600]/60 focus:ring-[#FFB600]/20"
                }`}
              />
              {emailError && (
                <p className="text-red-400 text-xs flex items-center gap-1">
                  <XCircle className="w-3 h-3 flex-shrink-0" />
                  {emailError}
                </p>
              )}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-[#FFB600] hover:bg-[#e6a400] text-[#001a00] px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? "Subscribing..." : "Subscribe"}
              </button>
            </form>
          </div>

          </div>{/* end 4-col wrapper */}
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-white/40">
            Copyright 2025 Ren Power Group Ltd. All rights reserved.
          </p>
          <a
            href="https://rextatech.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-[#FFB600] hover:text-[#FFD700] transition-colors duration-200"
          >
            Built by Rexta Technologies
          </a>
        </div>
      </div>

      {/* Newsletter Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        {subscriptionStatus === "success" ? (
          <div className="text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-[#002301] mb-2">
              Successfully Subscribed!
            </h3>
            <p className="text-gray-700 mb-6">
              Thank you for subscribing to our newsletter. You'll receive the latest solar news and updates in your inbox.
            </p>
            <button
              onClick={() => setIsModalOpen(false)}
              className="bg-[#FFB600] hover:bg-[#FFD700] text-[#002301] px-6 py-2 rounded-full font-medium transition-colors"
            >
              Got it!
            </button>
          </div>
        ) : (
          <div className="text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-[#002301] mb-2">
              Subscription Failed
            </h3>
            <p className="text-gray-700 mb-6">
              {errorMessage || "Please enter a valid email address and try again."}
            </p>
            <button
              onClick={() => {
                setIsModalOpen(false);
                setErrorMessage("");
              }}
              className="bg-[#FFB600] hover:bg-[#FFD700] text-[#002301] px-6 py-2 rounded-full font-medium transition-colors"
            >
              Try Again
            </button>
          </div>
        )}
      </Modal>
    </footer>
  );
};

export default Footer;
